import { useEffect, useState } from 'react';

/**
 * Whether the visitor has asked for reduced motion.
 *
 * Most reduced-motion checks on the detail pages are one-shot reads inside an
 * effect, which is enough to decline starting an animation. This returns the
 * value as state instead, for the cases that have to render something
 * different — an autoplaying video holding its poster frame, say, rather than
 * simply not moving.
 *
 * Starts false during SSR and re-reads on mount, so the server and the first
 * client render agree.
 */
export const usePrefersReducedMotion = (): boolean => {
    const [reduced, setReduced] = useState(() => {
        if (typeof window === 'undefined') {
            return false;
        }
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    });

    useEffect(() => {
        const query = window.matchMedia('(prefers-reduced-motion: reduce)');
        setReduced(query.matches);
        const onChange = () => setReduced(query.matches);
        query.addEventListener('change', onChange);
        return () => query.removeEventListener('change', onChange);
    }, []);

    return reduced;
};
